import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Grid from '@mui/material/Unstable_Grid2';
import { Box, Card, CardHeader, Chip, Container, Skeleton, Stack, Typography } from '@mui/material';

import { useSettings } from 'src/hooks/use-settings';
import useFeatureToggle from 'src/hooks/use-feature-toggle';
import { reformUsePhysicianTableV2 } from 'src/config';

import { Seo } from '../seo';
import PhysicianDocumentsTable from './physician-documents-table';
import PhysicianDocumentsTableV2 from './physician-documents-table-v2';

import { physicianConst } from './table-heads';

function PhysicianDashboard() {
  const location = useLocation();
  const signedDocumentIds = location.state?.signedDocumentIds || [];

  let [usePhysicianTableV2] = useFeatureToggle(reformUsePhysicianTableV2, 'ENTERPRISE');
  if (!usePhysicianTableV2) {
    const documentTableVersion = parseInt(import.meta.env.VITE_FF_PHYSICIAN_TABLE_VERSION) || 0;
    if (documentTableVersion === 2) {
      usePhysicianTableV2 = true;
    }
  }

  const settings = useSettings();
  const allDocumentsOfPhysician = useSelector((state: any) => state.physicianDocumentsReducer);
  const user = useSelector((state: any) => state.getTherapistProfileReducer?.response?.data);
  const completedPhysicianDocument = useSelector(
    (state: any) => state.completedPhysicianDocumentReducer
  );
  const [greeting, setGreeting] = useState('');

  useEffect(() => {
    const updateGreeting = () => {
      const currentHour = new Date().getHours();

      if (currentHour >= 5 && currentHour < 12) {
        setGreeting('Good Morning');
      } else if (currentHour >= 12 && currentHour < 17) {
        setGreeting('Good Afternoon');
      } else if (currentHour >= 17 && currentHour < 20) setGreeting('Good Evening');
      else {
        setGreeting('Good Night');
      }
    };

    updateGreeting();

    const intervalId = setInterval(updateGreeting, 60000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <>
      <Seo title="Restore-Insights: Physician" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
        }}
      >
        <Container maxWidth={settings.stretch ? false : 'xl'}>
          <Grid
            container
            disableEqualOverflow
            spacing={{
              xs: 3,
              lg: 4,
            }}
            mb={4}
          >
            <Grid xs={12}>
              <Stack>
                <Card>
                  <CardHeader
                    title={
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={4}
                      >
                        {!usePhysicianTableV2 && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="h5">Signature Pending</Typography>
                            {allDocumentsOfPhysician?.loading ? (
                              <Box sx={{ width: 100, ml: 3 }}>
                                <Typography
                                  width={'100%'}
                                  variant="h4"
                                >
                                  <Skeleton animation="pulse" />
                                </Typography>
                              </Box>
                            ) : (
                              <Chip
                                label={`${
                                  allDocumentsOfPhysician?.response?.data?.totalElements
                                    ? allDocumentsOfPhysician?.response?.data?.totalElements
                                    : '0'
                                } Document`}
                                variant="outlined"
                                sx={{ marginLeft: '10px' }}
                              />
                            )}
                          </Box>
                        )}
                      </Stack>
                    }
                  />
                  {usePhysicianTableV2 ? (
                    <PhysicianDocumentsTableV2
                      source={physicianConst.PENDING}
                      signedDocumentIds={signedDocumentIds}
                    />
                  ) : (
                    <PhysicianDocumentsTable source={physicianConst.PENDING} />
                  )}
                </Card>
              </Stack>
            </Grid>
            <Grid xs={12}>
              <Stack>
                <Card>
                  <CardHeader
                    title={
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={4}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="h5">Completed Documents</Typography>
                          {completedPhysicianDocument?.loading ? (
                            <Box sx={{ width: 100, ml: 3 }}>
                              <Typography
                                width={'100%'}
                                variant="h4"
                              >
                                <Skeleton animation="pulse" />
                              </Typography>
                            </Box>
                          ) : (
                            <Chip
                              label={`${
                                completedPhysicianDocument?.response?.data?.totalElements
                                  ? completedPhysicianDocument?.response?.data?.totalElements
                                  : '0'
                              } Document`}
                              variant="outlined"
                              sx={{ marginLeft: '10px' }}
                            />
                          )}
                        </Box>
                      </Stack>
                    }
                  />
                  <PhysicianDocumentsTable source={physicianConst.COMPLETED} />
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}

export default PhysicianDashboard;
